import { CycleRound, UserProfile } from "@ap-frontend/types"
import { cycles } from "@ap-frontend/questions"

import { parseDate } from "@ap-frontend/utility/dist/cjs/format-date"

interface ErrorOptions {
  cause?: unknown
}

export class InvalidUserProfileError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = this.constructor.name
  }
}

// TODO: extend validation to cover all significant fields. Maybe by defining a
//       JSON schema for the UserProfile type, and/or using a library like zod.

// To simplify testing and be explicit that this validation is incomplete, we
// reference a subset of the UserProfile that we do validate, not the whole thing.
export type UserProfileSubset = Pick<UserProfile, "deadline">

/**
 * Validate a UserProfile document for data validity.
 *
 * Currently, only a subset of the UserProfile fields are actually validated. The
 * UserProfileSubset type defines these fields.
 *
 * @throws {InvalidUserProfileError} when the profile does not meet validation requirements.
 */
export const validateUserProfile = (userProfile: UserProfileSubset): void => {
  try {
    validateDeadline(userProfile)
  } catch (cause) {
    throw new InvalidUserProfileError(
      `User Profile document failed validation: ${cause}`,
      { cause },
    )
  }
}

const validateDeadline = (profile: UserProfileDeadline) => {
  // To be valid, the deadline must parse as a Date, and correspond to a
  // CycleRound. The deadline must correspond to one of the rounds, so that
  // questions that vary based on the round can be correctly displayed.
  getCycleRound(profile)
}

type UserProfileDeadline = Pick<UserProfile, "deadline">

const getDeadlineDate = ({ deadline }: UserProfileDeadline): Date => {
  if (!deadline) {
    throw new InvalidUserProfileError("UserProfile deadline date is not set")
  }
  try {
    return parseDate(deadline)
  } catch (_e) {
    throw new InvalidUserProfileError(
      `UserProfile deadline date is not a date string: '${deadline}'`,
    )
  }
}

/**
 * Get the CycleRound that corresponds to a deadline date.
 *
 * @param deadline The deadline as a Date
 * @param profile The deadline from a UserProfile's deadline
 * @throws {Error} if a the deadline does not match a CycleRound
 */
export function getCycleRound(deadline: Date): CycleRound
export function getCycleRound(profile: UserProfileDeadline): CycleRound
export function getCycleRound(arg1: Date | UserProfileDeadline): CycleRound {
  const deadline = arg1 instanceof Date ? arg1 : getDeadlineDate(arg1)

  const round = cycles.cycleRounds.find(
    round => round.deadlineMonth === calendarMonth(deadline),
  )
  if (round) return round

  throw new Error(
    `Deadline month ${calendarMonth(deadline)} does not match any cycle round`,
  )
}

const calendarMonth = (date: Date): number => {
  return date.getMonth() + 1
}
